import React, { useState, useEffect, useRef } from 'react';
import NotificationService from '../../../services/NotificationService';
import { useSelector } from 'react-redux';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const consecutiveErrors = useRef(0);
  const pollingPaused = useRef(false);
  const resumeTimeout = useRef(null);

  const user = useSelector(state => state.auth.user);
  const isAdmin = user?.role === 'admin';

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        const response = await NotificationService.getAdminNotifications(20);
        if (response.success) {
          setNotifications(response.notifications);
        }
      } else {
        const response = await NotificationService.getNotifications(20);
        if (response.success) {
          setNotifications(response.notifications);
          setUnreadCount(response.unreadCount);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pause polling for 5 minutes after too many consecutive errors
  const pausePolling = () => {
    pollingPaused.current = true;
    if (resumeTimeout.current) clearTimeout(resumeTimeout.current);
    resumeTimeout.current = setTimeout(() => {
      pollingPaused.current = false;
      consecutiveErrors.current = 0;
    }, 5 * 60 * 1000); // 5 minutes
  };

  // Fetch unread count only
  const fetchUnreadCount = async () => {
    if (isAdmin) return; // Admin sees all notifications
    if (pollingPaused.current) return; // Back off when server is down
    try {
      const response = await NotificationService.getUnreadCount();
      if (response.success) {
        setUnreadCount(response.count);
        consecutiveErrors.current = 0; // reset on success
      }
    } catch (error) {
      consecutiveErrors.current += 1;
      if (consecutiveErrors.current >= 3) {
        pausePolling();
      }
      // Suppress noisy console spam when server is unreachable
    }
  };

  // Mark notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await NotificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    if (type.includes('stream_created')) return 'fa-video-camera';
    if (type.includes('stream_published')) return 'fa-play-circle';
    if (type.includes('stream_unpublished')) return 'fa-stop-circle';
    if (type.includes('stream_failed')) return 'fa-exclamation-triangle';
    if (type.includes('subscriber')) return 'fa-user-plus';
    if (type.includes('payment')) return 'fa-dollar-sign';
    if (type.includes('subscription')) return 'fa-credit-card';
    return 'fa-bell';
  };

  // Get notification color based on type
  const getNotificationColor = (type) => {
    if (type.includes('stream_created')) return 'text-primary';
    if (type.includes('stream_published')) return 'text-success';
    if (type.includes('stream_unpublished')) return 'text-warning';
    if (type.includes('stream_failed')) return 'text-danger';
    if (type.includes('subscriber')) return 'text-info';
    if (type.includes('payment')) return 'text-success';
    if (type.includes('subscription')) return 'text-primary';
    return 'text-secondary';
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll for new notifications
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();

    const interval = setInterval(() => {
      fetchUnreadCount();
      if (isOpen) {
        fetchNotifications();
      }
    }, 60000); // Poll every 60 seconds (reduced from 30s to ease server load)

    return () => {
      clearInterval(interval);
      if (resumeTimeout.current) clearTimeout(resumeTimeout.current);
    };
  }, [isOpen, isAdmin]);

  return (
    <div className="notification-bell-wrapper" ref={dropdownRef} style={{ position: 'relative' }}>
      <button 
        className="btn btn-link position-relative"
        onClick={() => setIsOpen(!isOpen)}
        style={{ color: 'inherit', textDecoration: 'none' }}
      >
        <i className="fa fa-bell" style={{ fontSize: '1.2rem' }}></i>
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div 
          className="dropdown-menu show"
          style={{ 
            position: 'absolute', 
            right: 0, 
            top: '100%', 
            width: '350px', 
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1050,
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
          }}
        >
          <div className="dropdown-header d-flex justify-content-between align-items-center p-3 border-bottom">
            <h6 className="mb-0">Notifications</h6>
            {unreadCount > 0 && !isAdmin && (
              <button 
                className="btn btn-sm btn-link"
                onClick={handleMarkAllAsRead}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="text-center p-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center p-3 text-muted">
                <i className="fa fa-bell-slash mb-2" style={{ fontSize: '2rem' }}></i>
                <p className="mb-0">No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`dropdown-item p-3 border-bottom ${!notification.is_read ? 'bg-light' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                >
                  <div className="d-flex align-items-start">
                    <div className={`me-3 ${getNotificationColor(notification.type)}`}>
                      <i className={`fa ${getNotificationIcon(notification.type)}`} style={{ fontSize: '1.2rem' }}></i>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-1 text-truncate" style={{ maxWidth: '200px' }}>
                          {notification.title}
                        </h6>
                        <small className="text-muted ms-2" style={{ whiteSpace: 'nowrap' }}>
                          {formatTime(notification.created_at)}
                        </small>
                      </div>
                      <p className="mb-0 text-muted small" style={{ 
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {notification.message}
                      </p>
                    </div>
                    {!notification.is_read && !isAdmin && (
                      <span className="ms-2">
                        <i className="fa fa-circle text-primary" style={{ fontSize: '0.5rem' }}></i>
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="dropdown-footer text-center p-2 border-top">
            <button 
              className="btn btn-sm btn-link text-muted"
              onClick={() => {
                setIsOpen(false);
                // Navigate to notifications page if exists
              }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
